// Design-project setup follows the real project accordion: project identity,
// four confirmed engineering-input sections, an engineering profile, and the
// DXF handoff. Figures and prose share the same Riverside fixture.
import type { HelpArticle } from "../../types";
import {
  PROJECT_HELP_PROJECT,
  PROJECT_HELP_SITE,
} from "../../../project/projectPreviewFixtures";

export const projectSetupArticle: HelpArticle = {
  id: "project-setup",
  title: "Project setup",
  description:
    "Create a design project, confirm its site and engineering basis in order, and understand what becomes read-only after the viewer handoff.",
  tags: ["design project", "setup", "site conditions", "engineering profile", "submission lock"],
  lastUpdated: "July 19, 2026",
  published: true,
  blocks: [
    {
      kind: "lead",
      text: "A design project is the controlled handoff from a named site to an identified DXF plan. Firmark does not treat the setup page as one long form: it asks you to review the project basis in a fixed sequence, records each confirmation separately, and keeps the viewer closed until both the confirmations and the Setup pipeline are complete.",
    },

    { kind: "heading", level: 2, text: "Create the project", id: "create" },
    { kind: "where", path: ["Design projects", "New project"] },
    { kind: "heading", level: 3, text: "Site and jurisdiction", id: "site-and-jurisdiction" },
    {
      kind: "paragraph",
      text: "The creation form establishes the identity and starting engineering context. **Project name** is required. The address is more than a cover-sheet label: selecting and confirming the mapped point gives the hazard report its latitude and longitude. Jurisdiction is stored separately because the authority that governs the work may not be identical to the postal city.",
    },
    {
      kind: "values",
      caption: "The deterministic Riverside Residence example used throughout this section.",
      rows: [
        { label: "Project", value: PROJECT_HELP_PROJECT.name },
        { label: "Site address", value: PROJECT_HELP_PROJECT.address ?? "" },
        { label: "Confirmed point", value: `${PROJECT_HELP_SITE.lat}, ${PROJECT_HELP_SITE.lng}` },
        { label: "Jurisdiction", value: PROJECT_HELP_PROJECT.jurisdiction ?? "" },
        { label: "Starting profile", value: PROJECT_HELP_PROJECT.profileName ?? "" },
      ],
    },
    {
      kind: "shot",
      shotId: "project-create-information",
      caption: "The project identity, confirmed site, jurisdiction, and starting engineering profile before creation.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Confirm the pin, not just the text",
      body: "A typed address without a confirmed map point is not enough for the automatic hazard report. If the site is remote or the geocoder selects the wrong parcel, move or confirm the point before generating hazards.",
    },
    { kind: "heading", level: 3, text: "Engineering profile", id: "engineering-profile" },
    {
      kind: "paragraph",
      text: "The engineering profile seeds the project's starting design policy. The selected profile and project-specific overrides remain reviewable on the project page before the viewer handoff.",
    },

    { kind: "heading", level: 2, text: "Work through the setup sequence", id: "sequence" },
    { kind: "where", path: ["Design projects", PROJECT_HELP_PROJECT.name, "Setup"] },
    {
      kind: "paragraph",
      text: "The project page opens the first incomplete section. Confirming Site conditions opens Hazard data; confirming hazards opens Soil; confirming Soil opens Foundation; confirming Foundation opens Engineering profile; confirming the profile reveals the Floor plan step. You can reopen any earlier section before the viewer handoff. A green check means that section was confirmed, not merely that a default happened to be present.",
    },
    {
      kind: "steps",
      steps: [
        {
          title: "Save Project info",
          body: "Review the project name, address, jurisdiction, description, and administrative identity. Saving the section advances to Site conditions.",
        },
        {
          title: "Confirm Site conditions",
          body: "Review the building and site modifiers, vertical geometry, and roof and ceiling inputs. Firmark saves the shared project input context and marks this section confirmed.",
        },
        {
          title: "Generate and confirm Hazard data",
          body: "The report is keyed to the confirmed point, risk category, soil class, and ASCE edition. Review the governing values, resolve any failed wind lookup, then use **Confirm hazard data**.",
        },
        {
          title: "Confirm Soil and Foundation separately",
          body: "Soil carries the basis, bearing properties, and optional layered profile. Foundation carries its class, subtype, and elevation or flood-datum inputs. They share one stored input context but keep separate review confirmations.",
        },
        {
          title: "Confirm Engineering profile",
          body: "Review the selected profile and its project overrides. The floor-plan handoff does not appear until this final engineering-basis confirmation is complete.",
        },
      ],
    },
    {
      kind: "shot",
      shotId: "project-setup-checklist",
      caption: "The ordered setup accordion: each engineering-input section carries its own review state.",
    },
    {
      kind: "paragraph",
      text: "Editing a confirmed input does not hide the change. For Site conditions, Soil, and Foundation, the action becomes **Save changes** when the shared input context is dirty. Editing, regenerating, or changing a confirmed hazard report clears its prior confirmation, because the data flowing downstream no longer matches what was acknowledged.",
    },

    { kind: "heading", level: 2, text: "The viewer handoff is a submission boundary", id: "submission-lock" },
    {
      kind: "paragraph",
      text: "Uploading the base DXF starts the Setup object-identification run. While it runs, the viewer action reads **Preparing…** and remains disabled. If the run fails, the same control offers **Retry setup**. When the run has produced its setup bundle and every required section is confirmed, **Open viewer** opens that run's identified plan.",
    },
    {
      kind: "paragraph",
      text: "Opening the project in the viewer records the submission and locks the portal inputs. The project page then shows a read-only banner, and its form controls are disabled. This prevents the portal basis from changing underneath a review already in progress. **Revise project** is the explicit escape hatch: confirm the revision and Firmark clears the lock so the inputs can be edited again.",
    },
    {
      kind: "shot",
      shotId: "project-submission-lock",
      caption: "After the viewer handoff, the portal shows the project lock and the explicit revision action.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "An upload is not readiness",
      body: "The viewer unlocks only when the required setup confirmations are complete and the Setup pipeline has produced its bundle. A recorded filename by itself does not satisfy either gate.",
    },

    { kind: "related", topics: ["hazard-reports", "floor-plans", "viewer-review"] },
  ],
};
